/**
 * Bootstraps an iframe by injecting a Gleap-hosted HTML document via about:blank + doc.write,
 * so the iframe inherits the parent page's origin instead of being a cross-site iframe to a
 * classified tracker domain.
 *
 * Why: Safari ITP applies restrictions (storage block, classification-based heuristics) to
 * iframes whose src is a classified tracker domain. By creating the iframe as about:blank and
 * writing the document content from the parent, the iframe becomes same-origin to the parent,
 * which Safari treats as first-party. The scripts inside the iframe still load from the
 * original Gleap origin, but as cross-origin script loads (not iframe navigations), which
 * ITP does not throttle.
 *
 * Requirements: the Gleap origin must serve the index.html with CORS (Access-Control-Allow-Origin).
 * `messenger-app.gleap.io` already does. For `outboundmedia.gleap.io` the helper will fall back
 * to direct iframe.src loading until CORS is enabled there.
 *
 * @param {HTMLIFrameElement} iframe - the iframe element (must already be appended to the DOM)
 * @param {string} url - the Gleap origin URL (e.g. https://messenger-app.gleap.io). May include a path.
 * @returns {void}
 */
export const bootstrapGleapFrame = (iframe, url) => {
  if (!iframe || !url) {
    return;
  }

  const fallbackToSrc = () => {
    try {
      iframe.src = url;
    } catch (e) {}
  };

  try {
    if (!iframe.contentDocument) return fallbackToSrc();
  } catch (e) {
    return fallbackToSrc();
  }

  // The asset base is the URL's origin + any path (e.g. https://outboundmedia.gleap.io/modal/).
  // We need a trailing slash so root-relative URLs resolve correctly via <base>.
  let baseHref;
  try {
    const parsed = new URL(url);
    // For a URL like https://outboundmedia.gleap.io/modal, assets are served from the origin root,
    // not from /modal/. So we use the origin as base. The path is just the entry HTML.
    baseHref = parsed.origin + '/';
  } catch (e) {
    return fallbackToSrc();
  }

  fetch(url, { mode: 'cors', credentials: 'omit' })
    .then((response) => {
      if (!response.ok) {
        throw new Error('Bootstrap fetch failed: ' + response.status);
      }
      return response.text();
    })
    .then((html) => {
      // Rewrite root-relative URLs (href="/x", src="/x") to absolute URLs on the Gleap origin,
      // so that the about:blank document (which has no base URL) can still resolve them.
      const absolutized = html.replace(
        /(\s(?:href|src)\s*=\s*["'])\/([^"'/][^"']*)/g,
        '$1' + baseHref + '$2'
      );

      // The Gleap apps (banner, modal, agent-conversation, chatbar) are SPAs that route based
      // on window.location.pathname. Since the iframe inherits the parent's origin via about:blank,
      // pathname would be the parent page's pathname — not the intended app route.
      // We use history.replaceState() to set the iframe's URL to the intended path BEFORE the
      // app's router initializes. replaceState is allowed because the iframe is same-origin to the parent.
      let targetPath = '/';
      try {
        const parsedTarget = new URL(url);
        targetPath = parsedTarget.pathname + parsedTarget.search + parsedTarget.hash;
      } catch (e) {}
      const routeScript =
        '<script>try{history.replaceState(null,"",' +
        JSON.stringify(targetPath) +
        ');}catch(e){}</script>';

      // Inject the route-setter script BEFORE the first existing <script> tag so it runs
      // before any app bundle. If no <script> is found, inject just before </head> as a fallback.
      let withRouteScript;
      if (/<script\b/i.test(absolutized)) {
        withRouteScript = absolutized.replace(/<script\b/i, routeScript + '<script');
      } else if (/<\/head>/i.test(absolutized)) {
        withRouteScript = absolutized.replace(/<\/head>/i, routeScript + '</head>');
      } else {
        withRouteScript = absolutized;
      }

      // Re-grab contentDocument here — Firefox may have replaced the initial about:blank
      // document during the fetch. Writing into the original reference would be a silent no-op.
      let doc;
      try {
        doc = iframe.contentDocument;
      } catch (e) {
        return fallbackToSrc();
      }
      if (!doc) {
        return fallbackToSrc();
      }

      try {
        doc.open();
        doc.write(withRouteScript);
        doc.close();
      } catch (e) {
        fallbackToSrc();
      }
    })
    .catch(() => {
      // CORS error, network error, or non-OK status: fall back to direct iframe.src loading.
      // This preserves the original behavior — the patch is a no-op when bootstrap can't work.
      fallbackToSrc();
    });
};

export const resizeImage = (base64Str, maxWidth = 400, maxHeight = 400) => {
  return new Promise((resolve, reject) => {
    var isJPEG = base64Str.indexOf('data:image/jpeg') === 0;
    var img = new Image();
    img.src = base64Str;
    img.onerror = () => {
      reject();
    };
    img.onload = () => {
      var canvas = document.createElement('canvas');
      var MAX_WIDTH = maxWidth;
      var MAX_HEIGHT = maxHeight;

      // Adjust max width / height based on image props
      if (maxWidth > img.width / 1.5) {
        MAX_WIDTH = img.width / 1.5;
      }

      if (maxHeight > img.height / 1.5) {
        MAX_HEIGHT = img.height / 1.5;
      }

      var width = img.width;
      var height = img.height;

      if (width > height) {
        if (width > MAX_WIDTH) {
          height *= MAX_WIDTH / width;
          width = MAX_WIDTH;
        }
      } else {
        if (height > MAX_HEIGHT) {
          width *= MAX_HEIGHT / height;
          height = MAX_HEIGHT;
        }
      }
      canvas.width = width;
      canvas.height = height;
      var ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      if (isJPEG) {
        resolve(canvas.toDataURL('image/jpeg', 0.7));
      } else {
        resolve(canvas.toDataURL());
      }
    };
  });
};

const MOBILE_UA_REGEX = /(android|iphone|ipod|ipad|blackberry|iemobile|opera mini|webos)/i;

export const getDeviceType = () => {
  return isMobile() ? 'mobile' : 'desktop';
};

export const isMobile = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const nav = typeof navigator !== 'undefined' ? navigator : undefined;

    if (nav?.userAgentData && typeof nav.userAgentData.mobile === 'boolean' && nav.userAgentData.mobile !== null) {
      return nav.userAgentData.mobile;
    }

    if (nav && MOBILE_UA_REGEX.test(nav.userAgent || nav.vendor || '')) {
      return true;
    }

    if (nav?.maxTouchPoints > 1 && window.matchMedia) {
      const coarsePointer = window.matchMedia('(hover: none) and (pointer: coarse)');
      if (coarsePointer?.matches) {
        return true;
      }
    }
  } catch (exp) {
    // Swallow errors and treat as desktop fallback.
  }

  return false;
};

export const gleapDataParser = function (data) {
  if (!data) {
    return {};
  }
  if (typeof data === 'string' || data instanceof String) {
    try {
      return JSON.parse(data);
    } catch (e) {
      return {};
    }
  }
  return data;
};

export const truncateString = (str, num) => {
  if (str.length > num) {
    return str.slice(0, num) + '...';
  } else {
    return str;
  }
};

const removeSubDomain = (v) => {
  try {
    var parts = v.split('.');
    var is2ndLevelDomain = false;
    const secondLevel = parts[parts.length - 2];
    if (
      secondLevel === 'co' ||
      secondLevel === 'com' ||
      secondLevel === 'gv' ||
      secondLevel === 'ac' ||
      secondLevel === 'edu' ||
      secondLevel === 'gov' ||
      secondLevel === 'mil' ||
      secondLevel === 'net' ||
      secondLevel === 'org'
    ) {
      is2ndLevelDomain = true;
    }
    parts = parts.slice(is2ndLevelDomain ? -3 : -2);
    return parts.join('.');
  } catch (exp) {}
  return v;
};

export const loadFromGleapCache = (key) => {
  try {
    const cachedData = localStorage.getItem(`gleap-widget-${key}`);
    if (cachedData) {
      const config = JSON.parse(cachedData);
      return config;
    }
  } catch (exp) {}
  return null;
};

export const saveToGleapCache = (key, data) => {
  const k = `gleap-widget-${key}`;
  if (data) {
    try {
      localStorage.setItem(k, JSON.stringify(data));
    } catch (exp) {}
  } else {
    localStorage.removeItem(k);
  }
};

export const clearGleapCache = (key) => {
  try {
    const k = `gleap-widget-${key}`;
    localStorage.removeItem(k);
  } catch {}
};

export const setGleapCookie = (name, value, days) => {
  try {
    var expires = '';
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    const host = removeSubDomain(window.location.host.split(':')[0]);
    document.cookie = name + '=' + (value || '') + expires + '; path=/; domain=' + host;
  } catch (exp) {}
};

export const getGleapCookie = (name) => {
  try {
    var nameEQ = name + '=';
    var ca = document.cookie.split(';');
    for (var i = 0; i < ca.length; i++) {
      var c = ca[i];
      while (c.charAt(0) == ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
    }
  } catch (exp) {}
  return null;
};

export const eraseGleapCookie = (name) => {
  try {
    const host = removeSubDomain(window.location.host.split(':')[0]);
    document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Domain=' + host;
  } catch (exp) {}
};

export const getDOMElementDescription = (element, html = true) => {
  var innerText = truncateString(element.innerText || '', 40)
    .replace(/(\r\n|\n|\r)/gm, '')
    .replace(/ +(?= )/g, '');
  var elementId = '';
  var elementClass = '';
  if (typeof element.getAttribute !== 'undefined') {
    const elemId = element.getAttribute('id');
    if (elemId) {
      elementId = ` id="${elemId}"`;
    }
    const elemClass = element.getAttribute('class');
    if (elemClass) {
      elementClass = ` class="${elemClass}"`;
    }
  }
  const elementTag = (element.tagName || '').toLowerCase();

  var htmlPre = '<';
  var htmlPost = '>';
  if (!html) {
    htmlPre = '[';
    htmlPost = ']';
  }

  return `${htmlPre}${elementTag}${elementId}${elementClass}${htmlPost}${innerText}${htmlPre}/${elementTag}${htmlPost}`;
};

export const runFunctionWhenDomIsReady = (callback) => {
  if (document.readyState === 'complete' || document.readyState === 'loaded' || document.readyState === 'interactive') {
    callback();
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      callback();
    });
  }
};

export const fixGleapHeight = () => {
  try {
    if ('visualViewport' in window && /iPad|iPhone|iPod/.test(navigator.userAgent)) {
      let initialHeight = window.innerHeight;

      function updateContainerHeight() {
        try {
          const gleapFrameContainer = document.querySelector('.gleap-frame-container-inner iframe');

          if (!gleapFrameContainer) {
            return;
          }

          // Check if the keyboard is open
          if (window.visualViewport.height < initialHeight) {
            gleapFrameContainer.style.setProperty('max-height', window.visualViewport.height + 'px', 'important');
          } else {
            // Remove the padding bottom
            gleapFrameContainer.style.removeProperty('max-height');
          }
        } catch (error) {}
      }

      function handleOrientationChange() {
        try {
          // Update initial dimensions
          initialHeight = window.innerHeight;
          updateContainerHeight();
        } catch (error) {}
      }

      // Update on resize (keyboard show/hide and viewport resize)
      window.visualViewport.addEventListener('resize', updateContainerHeight);

      // Handle orientation changes
      window.addEventListener('orientationchange', handleOrientationChange);

      // Update initially
      updateContainerHeight();
    }
  } catch (error) {}
};
